// src/pages/Users/UsersController.ts
import {useState, useEffect} from 'react'
import {getUsers, getUserActivity} from '../../services/Users'
import {User, UserActivity} from '../../types'
import {SortField, SortOrder} from './UsersModel'
import {api} from '../../services/api'

export const useUsersController = () => {
    const [users, setUsers] = useState<User[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [selectedUser, setSelectedUser] = useState<User | null>(null)
    const [userActivity, setUserActivity] = useState<UserActivity | null>(null)
    const [activityLoading, setActivityLoading] = useState(false)
    const [sortField, setSortField] = useState<SortField>('created_at')
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
    const [sidebarOpen, setSidebarOpen] = useState(false)

    // للـ Dialog
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [userToToggle, setUserToToggle] = useState<User | null>(null)

    // 👇 رسالة toast الجديدة
    const [toastMessage, setToastMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)

    useEffect(() => {
        fetchUsers()
    }, [])

    const fetchUsers = async () => {
        try {
            const data = await getUsers()
            console.log('📥 Users fetched:', data)

            // ✅ تأكد من أن كل مستخدم عنده is_active
            const usersWithStatus = data.map(user => ({
                ...user,
                is_active: user.is_active ?? 1 // إذا ما في is_active، اعتبره 1
            }))

            setUsers(usersWithStatus)
        } catch (error) {
            console.error('Error fetching users:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleViewActivity = async (user: User) => {
        setSelectedUser(user)
        setActivityLoading(true)
        try {
            const activity = await getUserActivity(user.id)
            setUserActivity(activity)
        } catch (error) {
            console.error('Error fetching user activity:', error)
        } finally {
            setActivityLoading(false)
        }
    }

    // فتح Dialog الحظر/التفعيل
    const handleToggleUserStatus = (user: User) => {
        console.log('🔧 Toggling user:', user)
        setUserToToggle(user)
        setShowConfirmDialog(true)
    }

    // تأكيد العملية - مع التعديل 👇
    const confirmToggleUserStatus = async () => {
        if (!userToToggle) return

        setShowConfirmDialog(false)

        try {
            const newStatus = userToToggle.is_active === 1 ? 0 : 1
            console.log('🔄 Changing status to:', newStatus)

            const response = await api.post(`/api/admin/users/${userToToggle.id}/toggle-status`, {
                is_active: newStatus
            })

            console.log('✅ Response:', response.data)

            // ✅ التحقق من الاستجابة
            if (response.data?.success === true) {
                // تحديث القائمة
                setUsers(prev => {
                    const updatedUsers = prev.map(u =>
                        u.id === userToToggle.id
                            ? {...u, is_active: newStatus}
                            : u
                    )
                    console.log('✅ Users updated:', updatedUsers)
                    return updatedUsers
                })

                // 👇 رسالة نجاح
                setToastMessage({
                    type: 'success',
                    text: `تم ${newStatus === 1 ? 'تفعيل' : 'حظر'} المستخدم بنجاح`
                })
                setTimeout(() => setToastMessage(null), 3000)
            } else {
                // 👇 رسالة خطأ
                setToastMessage({
                    type: 'error',
                    text: response.data?.error || 'حدث خطأ في العملية'
                })
                setTimeout(() => setToastMessage(null), 3000)
            }
        } catch (error: any) {
            console.error('❌ Error:', error)

            // 👇 رسالة خطأ
            setToastMessage({
                type: 'error',
                text: error.response?.data?.error || 'حدث خطأ في الاتصال'
            })
            setTimeout(() => setToastMessage(null), 3000)
        } finally {
            setUserToToggle(null)
        }
    }

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')
        } else {
            setSortField(field)
            setSortOrder('desc')
        }
    }

    const filteredAndSortedUsers = users
        .filter(user =>
            user.phone_number.includes(search) ||
            user.id.includes(search) ||
            (user.device_type && user.device_type.toLowerCase().includes(search.toLowerCase()))
        )
        .sort((a, b) => {
            let comparison = 0
            if (sortField === 'balance_usd') {
                comparison = a.balance_usd - b.balance_usd
            } else if (sortField === 'created_at') {
                comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            } else {
                comparison = a.phone_number.localeCompare(b.phone_number)
            }
            return sortOrder === 'desc' ? -comparison : comparison
        })

    const closeModal = () => {
        setSelectedUser(null)
        setUserActivity(null)
    }

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen)
    }

    return {
        // State
        users,
        filteredAndSortedUsers,
        loading,
        search,
        selectedUser,
        userActivity,
        activityLoading,
        sortField,
        sortOrder,
        sidebarOpen,
        showConfirmDialog,
        userToToggle,
        toastMessage,           // 👇 أضفنا هذا

        // Setters
        setSearch,
        setShowConfirmDialog,
        setUserToToggle,
        setToastMessage,        // 👇 وأضفنا هذا

        // Actions
        handleViewActivity,
        handleSort,
        handleToggleUserStatus,
        confirmToggleUserStatus,
        closeModal,
        toggleSidebar
    }
}